export type AnimationStyle = 'breathe' | 'sweep' | 'bloom' | 'sway' | 'lifecycle';

interface DutchieIconProps {
  size?: number;
  color?: string;
  animate?: boolean | AnimationStyle;
}

function getAnimationCSS(style: AnimationStyle): string {
  const s = style; // short alias for class prefixes
  switch (style) {
    case 'breathe':
      return `
        @keyframes di-${s} {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.35; }
        }
        .di-${s}-leaf-1 { animation: di-${s} 2s ease-in-out infinite; }
        .di-${s}-leaf-2 { animation: di-${s} 2s ease-in-out 0.4s infinite; }
        .di-${s}-leaf-3 { animation: di-${s} 2s ease-in-out 0.8s infinite; }
      `;

    case 'sweep':
      return `
        @keyframes di-${s} {
          0%, 100% { opacity: 0.25; }
          20% { opacity: 1; }
          45% { opacity: 0.25; }
        }
        @keyframes di-${s}-center {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        .di-${s}-leaf-1 { animation: di-${s} 2.4s ease-in-out infinite; }
        .di-${s}-leaf-2 { animation: di-${s} 2.4s ease-in-out 0.8s infinite; }
        .di-${s}-leaf-3 { animation: di-${s} 2.4s ease-in-out 1.6s infinite; }
        .di-${s}-center { animation: di-${s}-center 2.4s ease-in-out infinite; }
      `;

    case 'bloom':
      return `
        @keyframes di-${s} {
          0%, 100% { transform: scale(1) rotate(0deg); }
          50% { transform: scale(1.08) rotate(3deg); }
        }
        @keyframes di-${s}-center {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(0.92); }
        }
        .di-${s}-leaf { transform-origin: 17px 16px; }
        .di-${s}-leaf-1 { animation: di-${s} 2.5s ease-in-out infinite; }
        .di-${s}-leaf-2 { animation: di-${s} 2.5s ease-in-out 0.3s infinite; }
        .di-${s}-leaf-3 { animation: di-${s} 2.5s ease-in-out 0.6s infinite; }
        .di-${s}-center { transform-origin: 17px 16px; animation: di-${s}-center 2.5s ease-in-out infinite; }
      `;

    case 'sway':
      return `
        @keyframes di-${s}-1 {
          0%, 100% { transform: translate(0, 0) rotate(0deg); opacity: 1; }
          50% { transform: translate(1.3px, -1px) rotate(1.5deg); opacity: 0.7; }
        }
        @keyframes di-${s}-2 {
          0%, 100% { transform: translate(0, 0) rotate(0deg); opacity: 1; }
          50% { transform: translate(-1.2px, -1.3px) rotate(-2deg); opacity: 0.7; }
        }
        @keyframes di-${s}-3 {
          0%, 100% { transform: translate(0, 0) rotate(0deg); opacity: 1; }
          50% { transform: translate(-1px, 1.2px) rotate(1.8deg); opacity: 0.7; }
        }
        @keyframes di-${s}-center {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(0.95); opacity: 0.8; }
        }
        .di-${s}-leaf { transform-origin: 17px 16px; }
        .di-${s}-leaf-1 { animation: di-${s}-1 3s ease-in-out infinite; }
        .di-${s}-leaf-2 { animation: di-${s}-2 3s ease-in-out 0.5s infinite; }
        .di-${s}-leaf-3 { animation: di-${s}-3 3s ease-in-out 1s infinite; }
        .di-${s}-center { transform-origin: 17px 16px; animation: di-${s}-center 2s ease-in-out infinite; }
      `;

    case 'lifecycle':
      return `
        @keyframes di-${s}-svg {
          0%, 35%  { transform: rotate(0deg); opacity: 0.45; }
          55%      { transform: rotate(360deg); opacity: 1; }
          75%, 100%{ transform: rotate(360deg); opacity: 0.45; }
        }
        @keyframes di-${s}-center {
          0%, 5%   { transform: scale(0); }
          10%      { transform: scale(1.15); }
          15%, 30% { transform: scale(1); }
          35%      { transform: scale(1); }
          40%      { transform: scale(0.88); }
          45%      { transform: scale(1); }
          50%      { transform: scale(0.88); }
          55%, 90% { transform: scale(1); }
          98%, 100%{ transform: scale(0); }
        }
        @keyframes di-${s}-1 {
          0%, 15%  { transform: scale(0) rotate(0deg); }
          22%      { transform: scale(1.12) rotate(-4deg); }
          28%, 75% { transform: scale(1) rotate(0deg); }
          81%      { transform: scale(1) rotate(0deg); }
          88%, 100%{ transform: scale(0) rotate(8deg); }
        }
        @keyframes di-${s}-2 {
          0%, 18%  { transform: scale(0) rotate(0deg); }
          25%      { transform: scale(1.1) rotate(5deg); }
          30%, 75% { transform: scale(1) rotate(0deg); }
          78%      { transform: scale(1) rotate(0deg); }
          85%, 100%{ transform: scale(0) rotate(-6deg); }
        }
        @keyframes di-${s}-3 {
          0%, 21%  { transform: scale(0) rotate(0deg); }
          28%      { transform: scale(1.08) rotate(-3deg); }
          33%, 75% { transform: scale(1) rotate(0deg); }
          75%      { transform: scale(1) rotate(0deg); }
          82%, 100%{ transform: scale(0) rotate(5deg); }
        }
        .di-${s}-svg { animation: di-${s}-svg 7s ease-in-out infinite; transform-origin: center; }
        .di-${s}-leaf { transform-origin: 17px 16px; }
        .di-${s}-leaf-1 { animation: di-${s}-1 7s ease-in-out infinite; transform-origin: 17px 16px; }
        .di-${s}-leaf-2 { animation: di-${s}-2 7s ease-in-out infinite; transform-origin: 17px 16px; }
        .di-${s}-leaf-3 { animation: di-${s}-3 7s ease-in-out infinite; transform-origin: 17px 16px; }
        .di-${s}-center { animation: di-${s}-center 7s ease-in-out infinite; transform-origin: 17px 16px; }
      `;
  }
}

export function DutchieIcon({ size = 30, color = '#1a6b4a', animate = false }: DutchieIconProps) {
  const animStyle: AnimationStyle | null = animate === true ? 'sway' : animate || null;

  const leafCls = (n: number) =>
    animStyle ? `di-${animStyle}-leaf di-${animStyle}-leaf-${n}` : undefined;
  const centerCls = animStyle ? `di-${animStyle}-center` : undefined;
  const svgCls = animStyle === 'lifecycle' ? `di-${animStyle}-svg` : undefined;

  return (
    <svg
      className={svgCls}
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 33 33"
      fill="none"
    >
      {animStyle && <style>{getAnimationCSS(animStyle)}</style>}
      <path
        className={leafCls(1)}
        d="M30.2095 22.8152C28.5733 23.6972 26.7956 24.3587 25.0887 24.7496C23.1394 25.1907 21.1395 25.341 19.1498 25.1906C15.8066 24.91 12.5645 23.3865 10.3323 20.8808C10.0596 20.5801 9.83743 20.2093 9.41322 20.109C8.73651 19.9587 8.2618 20.4197 8.1002 21.0311C6.45387 27.095 12.0494 32.8782 18.1802 32.3771C23.6949 32.0463 28.7854 28.849 31.4215 24.028C31.5831 23.7474 31.573 23.4266 31.4114 23.156C31.169 22.735 30.6337 22.5747 30.2095 22.8152Z"
        fill={color}
      />
      <path
        className={leafCls(2)}
        d="M16.4127 1.74706C17.9984 2.71928 19.473 3.912 20.6649 5.18491C22.0284 6.63823 23.1596 8.28199 24.0282 10.0661C25.4523 13.083 25.7351 16.621 24.6746 19.7983C24.5433 20.1892 24.3312 20.56 24.4625 20.971C24.6746 21.6225 25.3008 21.8029 25.927 21.6425C32.0478 20.0188 34.29 12.3312 30.7852 7.30977C27.735 2.7293 22.4021 -0.037019 16.8773 0.103302C16.5541 0.113325 16.2713 0.273693 16.1198 0.544312C15.8673 0.955251 15.9986 1.50651 16.4127 1.74706Z"
        fill={color}
      />
      <path
        className={centerCls}
        d="M17.8772 11.6095C16.6349 11.4291 15.3824 11.7298 14.3724 12.4715C13.8472 12.8524 13.4129 13.3335 13.0897 13.8948C12.1504 15.5084 12.2615 17.5231 13.3826 19.0165C14.13 20.0188 15.2309 20.6803 16.4733 20.8607C17.7156 21.0411 18.968 20.7404 19.978 19.9987C20.5032 19.6178 20.9375 19.1367 21.2607 18.5755C22.2001 16.9618 22.089 14.9472 20.9678 13.4538C20.2305 12.4515 19.1296 11.79 17.8772 11.6095Z"
        fill={color}
      />
      <path
        className={leafCls(3)}
        d="M16.5541 8.02138C16.9581 7.9412 17.3924 7.93118 17.6853 7.62047C18.1499 7.1093 17.9984 6.47785 17.5338 6.02682C13.0594 1.57665 5.22166 3.50105 2.59561 9.02367C0.131169 13.9349 0.373578 19.8985 3.26223 24.5792C3.43393 24.8599 3.70664 25.0102 4.02984 25.0102C4.51465 25.0102 4.91866 24.6293 4.91866 24.1482C4.96916 22.304 5.27216 20.4398 5.78727 18.776C6.37308 16.8816 7.2417 15.0875 8.37292 13.4438C10.3021 10.7075 13.2513 8.69292 16.5541 8.02138Z"
        fill={color}
      />
    </svg>
  );
}
